
import React, { useEffect, useRef, useState, useCallback } from 'react';

// Import local images
import image4 from "../public/images/home/woman-smiling.png";
import image7 from "../public/images/home/person-holding.png";
import image8 from "../public/images/home/personholdingfamily.png";
import image2 from "../public/images/home/womanplacingmagnet.png";
import image3 from "../public/images/home/personwritingnote.png";
import image5 from "../public/images/home/personscanningqrcode.png";
import image6 from "../public/images/home/arrangngsemtimental.png";
import image1 from "../public/images/home/familymemberslooking.png";

const wrap = (value, length) =>
  ((value % length) + length) % length;

const shortestLoopDistance = (value, length) =>
  wrap(value + length / 2, length) - length / 2;

const InfiniteScrollingGallery = () => {
  const viewportRef = useRef(null);
  const cardRefs = useRef([]);
  const originalCount = 8;

  const stateRef = useRef({
    position: 0,
    target: 0,
    velocity: 0.0002,
    dragging: false,
    paused: false,
    pointerStart: 0,
    positionStart: 0,
    lastTime: 0,
    cardWidth: 250,
    cardHeight: 320,
  });

  const [isDragging, setIsDragging] = useState(false);

  const images = [
    { id: 1, src: image1, alt: 'Family members looking together' },
    { id: 2, src: image2, alt: 'Woman placing a magnet' },
    { id: 3, src: image3, alt: 'Person writing a note' },
    { id: 4, src: image4, alt: 'Woman smiling' },
    { id: 5, src: image5, alt: 'Person scanning a QR code' },
    { id: 6, src: image6, alt: 'Arranging sentimental items' },
    { id: 7, src: image7, alt: 'Person holding a keepsake' },
    { id: 8, src: image8, alt: 'Person holding family photos' },
  ];

  // Eight originals plus eight duplicates for infinite scrolling.
  const allImages = [...images, ...images];

  const render = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const cards = cardRefs.current;
    const count = cards.length;
    const viewportWidth = viewport.clientWidth;
    const s = stateRef.current;

    const layoutScale = Math.max(
      0.75,
      Math.min(1, viewportWidth / 1100)
    );

    const visibleRadius = viewportWidth < 700 ? 2.6 : 4.2;

    cards.forEach((card, index) => {
      if (!card) return;

      const raw = index - s.position;
      const orbitOffset = shortestLoopDistance(raw, count);
      const distance = Math.abs(orbitOffset);
      const direction = Math.sign(orbitOffset);
      const curve = Math.min(distance, 4.6);

      // Consistent gap between cards.
      const gap = 5;
      const spacing = s.cardWidth + gap;

      // Soft U-shaped depth:
      // center cards recede, side cards move forward.
      const depth =
        -300 + Math.min(220, 25 * curve * curve);

      // Gentle 3D rotation.
      const rotateY =
        -direction * Math.min(25, 3 * curve * curve);

      // Horizontal spread with extra room toward the edges.
      const x =
        direction *
        (
          spacing * curve +
          -2 * curve * curve * curve +
          95 * Math.max(0, curve - 3) ** 1
        ) *
        layoutScale;

      // Fade cards outside the visible area.
      const opacity = Math.max(
        0,
        1 - Math.max(0, distance - visibleRadius) * 2
      );

      // Every card has the same actual dimensions.
      card.style.width = `${s.cardWidth}px`;
      card.style.height = `${s.cardHeight}px`;
      card.style.marginLeft = `${-s.cardWidth / 2}px`;
      card.style.marginTop = `${-s.cardHeight / 2}px`;

      card.style.transform =
        `translate3d(${x}px, 0, ${depth}px) rotateY(${rotateY}deg)`;

      card.style.opacity = String(opacity);
      card.style.zIndex = String(
        Math.round(100 - distance * 10)
      );

      card.setAttribute(
        'aria-hidden',
        index >= originalCount || distance > visibleRadius
          ? 'true'
          : 'false'
      );
    });
  }, []);

  // Infinite animation loop.
  useEffect(() => {
    let rafId;

    stateRef.current.lastTime = performance.now();

    const animate = (now) => {
      const s = stateRef.current;
      const delta = Math.min(40, now - s.lastTime);

      s.lastTime = now;

      if (!s.dragging) {
        if (!s.paused) {
          s.target += s.velocity * delta;
        }

        s.position += (s.target - s.position) * 0.075;
      }

      render();
      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(rafId);
  }, [render]);

  // Update card dimensions responsively.
  useEffect(() => {
    const updateSize = () => {
      const viewport = viewportRef.current;
      if (!viewport) return;

      const vw = viewport.clientWidth;

      const cardWidth = Math.max(
        190,
        Math.min(250, vw * 0.22)
      );

      stateRef.current.cardWidth = cardWidth;
      stateRef.current.cardHeight = cardWidth * 1.28;

      render();
    };

    updateSize();

    window.addEventListener('resize', updateSize);

    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, [render]);

  // Drag to scroll.
  const handlePointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    const viewport = viewportRef.current;
    if (!viewport) return;

    const s = stateRef.current;

    s.dragging = true;
    s.pointerStart = e.clientX;
    s.positionStart = s.position;

    setIsDragging(true);

    viewport.classList.add('is-dragging');
    viewport.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    const viewport = viewportRef.current;
    const s = stateRef.current;

    if (!s.dragging || !viewport) return;

    const pixelsPerCard = s.cardWidth + 32;

    s.position =
      s.positionStart -
      (e.clientX - s.pointerStart) / pixelsPerCard;

    s.target = s.position;
  };

  const releasePointer = () => {
    const s = stateRef.current;

    if (!s.dragging) return;

    s.dragging = false;
    s.target = s.position;

    setIsDragging(false);

    viewportRef.current?.classList.remove('is-dragging');
  };

  // Respect the user's reduced-motion preference.
  useEffect(() => {
    const mediaQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    const updateMotionPreference = () => {
      stateRef.current.paused = mediaQuery.matches;
    };

    updateMotionPreference();

    mediaQuery.addEventListener?.(
      'change',
      updateMotionPreference
    );

    return () => {
      mediaQuery.removeEventListener?.(
        'change',
        updateMotionPreference
      );
    };
  }, []);

  return (
    <section
      className=" w-full  select-none"
      aria-label="3D curved image gallery"
    >
      <div
        ref={viewportRef}
        className={`relative w-full overflow-hidden touch-pan-y ${
          isDragging ? 'is-dragging' : 'cursor-grab'
        }`}
        style={{
          height: '320px',
          perspective: '1200px',
          perspectiveOrigin: '50% 50%',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={releasePointer}
        onPointerCancel={releasePointer}
        onLostPointerCapture={releasePointer}
      >
        {allImages.map((image, index) => (
          <div
            key={`${image.id}-${index}`}
            ref={(el) => {
              cardRefs.current[index] = el;
            }}
            role="img"
            aria-label={image.alt}
            className="absolute top-1/2 left-1/2 overflow-hidden  bg-[#e9e5e4] will-change-transform"
            style={{
              transformStyle: 'preserve-3d',
              backfaceVisibility: 'hidden',
            }}
          >
            <img
              src={
                typeof image.src === 'string'
                  ? image.src
                  : image.src.src || image.src
              }
              alt={image.alt}
              className="block w-full h-full object-cover pointer-events-none"
              loading="lazy"
              draggable={false}
            />
          </div>
        ))}
      </div>

      <style jsx>{`
        .is-dragging {
          cursor: grabbing;
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            transition: none !important;
          }
        }
      `}</style>
    </section>
  );
};

export default InfiniteScrollingGallery;
